import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { PermissionsApiService } from './permissions-api.service';
import { PermissionLevel } from './models/permission-level.type';
import { AuthService } from '../auth/auth.service';

const STORAGE_KEY = 'ResolvedPermissions';

// Cache w pamięci + sessionStorage (analogicznie do LoggedInUser w
// localStorage) - żeby odświeżenie strony nie wymagało ponownego
// zalogowania zanim mapa uprawnień się doładuje. Rozstrzyganie poziomu
// (rola -> nadpisanie -> dziedziczenie z rodzica) liczone jest WYŁĄCZNIE po
// stronie backendu (PermissionsResolverService) - tu tylko czytamy gotową,
// spłaszczoną mapę.
//
// Stan trzymany jako BehaviorSubject (nie zwykłe pole) - komponenty typu
// MenuComponent montują się natychmiast przy starcie appki, ZANIM refresh()
// (async, wywołane w AppComponent.ngOnInit) zdąży się skończyć. Gdyby
// HasPermissionDirective czytała tylko statyczne pole, oceniłaby pustą mapę
// przy pierwszym renderze i już nigdy by się nie przeliczyła po
// dociągnięciu danych (błąd zgłoszony 2026-08-12: "po rozwinięciu
// administracji nie ma nic do wybrania" - menu wystartowało przed refresh(),
// dyrektywa nie miała jak się dowiedzieć że dane dotarły).
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly initialSnapshot = this.readFromSessionStorage();
  private readonly resolved$ = new BehaviorSubject<Record<string, PermissionLevel>>(
    this.initialSnapshot,
  );

  // true, gdy mapa uprawnien zostala juz PRZYNAJMNIEJ RAZ zaladowana w tej
  // karcie (z sessionStorage przy starcie, albo po pierwszym udanym/nieudanym
  // refresh()). PermissionGuard czeka na to zanim zdecyduje - inaczej otwarcie
  // gate'owanej trasy w NOWEJ karcie (np. link "otworz w nowym oknie" z target=
  // _blank) moglo trafic na guard ZANIM AppComponent.ngOnInit() zdazyl wywolac
  // refresh(), i przekierowac na home mimo ze user ma pelne uprawnienia
  // (zgloszenie 25.08 - link do faktury z "Dokumentow powiazanych" otwieral
  // pusta karte na "#/").
  private readonly loaded$ = new BehaviorSubject<boolean>(
    Object.keys(this.initialSnapshot).length > 0,
  );
  private inFlight: Promise<void> | null = null;

  constructor(
    private readonly permissionsApi: PermissionsApiService,
    private readonly authService: AuthService,
  ) {}

  /** Emituje przy każdej zmianie mapy - HasPermissionDirective subskrybuje to, żeby przeliczyć się gdy dane dotrą asynchronicznie. */
  get changes(): Observable<Record<string, PermissionLevel>> {
    return this.resolved$.asObservable();
  }

  private readFromSessionStorage(): Record<string, PermissionLevel> {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw) ?? {};
    } catch {
      return {};
    }
  }

  refresh(): Promise<void> {
    // Kilka rownoleglych wywolan (np. AppComponent.ngOnInit() i
    // PermissionGuard.ensureLoaded() na tym samym cold-boot) czeka na TEN SAM
    // request zamiast strzelac duplikatami.
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.doRefresh().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async doRefresh(): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      this.resolved$.next({});
      sessionStorage.removeItem(STORAGE_KEY);
      this.loaded$.next(true);
      return;
    }

    try {
      const resolved = await this.permissionsApi.getResolved(userId).toPromise();
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(resolved));
      this.resolved$.next(resolved);
    } catch {
      // Brak połączenia z API nie powinien wywalić całej aplikacji - user
      // zostaje z ostatnią znaną mapą (albo pustą, jeśli jej jeszcze nie ma).
    } finally {
      this.loaded$.next(true);
    }
  }

  /** Rozstrzyga natychmiast, jesli mapa byla juz przynajmniej raz zaladowana
   *  (z sessionStorage albo z wczesniejszego refresh()) - inaczej wola
   *  refresh() i czeka na jego zakonczenie. Do uzycia w PermissionGuard. */
  ensureLoaded(): Observable<boolean> {
    if (this.loaded$.value) {
      return of(true);
    }
    void this.refresh();
    return this.loaded$.pipe(filter((loaded): loaded is true => loaded), take(1));
  }

  clear(): void {
    this.resolved$.next({});
    this.loaded$.next(true);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  has(resourceKey: string, level: 'read' | 'write' = 'read'): boolean {
    const current = this.resolved$.value[resourceKey] ?? 'none';
    if (current === 'none') {
      return false;
    }
    if (level === 'read') {
      return current === 'read' || current === 'write';
    }
    return current === 'write';
  }
}
